import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { http } from '@/lib/api/client'
import type { BillingEventList, BillingEventStatus, Resource, Sales, SubscriptionList } from '@/lib/api/types'

/** Opening sales is recorded in the staff audit log. */
export function useSales(months = 12) {
  return useQuery({
    queryKey: ['sales', months],
    queryFn: async () => (await http.get<Resource<Sales>>('/sales', { months })).data,
    placeholderData: keepPreviousData,
  })
}

export interface SubscriptionFilters {
  state: string
  kind: string
  plan: string
  interval: string
  renews: string
  search: string
  page: number
}

export function useSubscriptions(filters: SubscriptionFilters) {
  return useQuery({
    queryKey: ['subscriptions', filters],
    queryFn: () =>
      http.get<SubscriptionList>('/subscriptions', {
        state: filters.state || undefined,
        kind: filters.kind || undefined,
        plan: filters.plan || undefined,
        interval: filters.interval || undefined,
        renews: filters.renews || undefined,
        search: filters.search || undefined,
        page: filters.page,
      }),
    placeholderData: keepPreviousData,
  })
}

export function useBillingEvents(status: BillingEventStatus | '', page: number) {
  return useQuery({
    queryKey: ['billing-events', status, page],
    queryFn: () => http.get<BillingEventList>('/billing-events', { status: status || undefined, page }),
    placeholderData: keepPreviousData,
  })
}

/** Owners only: run a failed webhook again once its cause is fixed. */
export function useRetryBillingEvent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => http.post(`/billing-events/${id}/retry`),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['billing-events'] }),
        queryClient.invalidateQueries({ queryKey: ['subscriptions'] }),
        queryClient.invalidateQueries({ queryKey: ['sales'] }),
      ]),
  })
}
